import { Field, useFormikContext } from 'formik';
import { useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { FieldProps } from '../../types/fields';
import callApi from '../../utils/callApi';
import { config } from '../../utils/config';
import { Country } from '../../utils/countryData';
import debounce from '../../utils/debounce';
import getValidator from '../../utils/getValidator';
import { reportErrorToSentry } from '../../utils/initializeSentry';
import TextField from '../formik/TextField';
import { showDefaultError } from '../molecules/Notifications';

export type AddressCompletion = {
  city: string;
  street: string;
  zipCode: string;
  houseNumber: string;
};

type AddressFieldNlProps = Omit<FieldProps, 'context'> & {
  context: 'shippingAddress' | 'billingAddress';
};

const AdressFieldNl = (props: AddressFieldNlProps) => {
  const { formatMessage } = useIntl();
  const { values, validateField, setFieldValue } = useFormikContext<any>();

  const selectedCountry: Country = values[props.context].country;
  const zipCode = useRef('');
  const houseNumber = useRef('');

  const {
    additional: additionalConfig,
    streetHouseNumber: { houseNumber: houseNumberConfig, street: streetConfig },
    zipCodeCity: { zipCode: zipCodeConfig, city: cityConfig },
  } = config.addressForms[selectedCountry];

  const getAutofillSuggestions = async () => {
    try {
      const zipCodeErrors = (await validateField(props.context + '.zipCode')) as any;
      const houseNumberErrors = (await validateField(props.context + '.houseNumber')) as any;

      if (zipCodeErrors || houseNumberErrors) {
        return;
      }

      const autofillResponse = await callApi<AddressCompletion[]>({
        endpoint: '/address/suggest',
        urlParams: { zipCode: zipCode.current, houseNumber: houseNumber.current },
        flow: 'legacy',
      });

      if (autofillResponse.length) {
        setFieldValue(props.context + '.city', autofillResponse[0].city);
        setFieldValue(props.context + '.street', autofillResponse[0].street);
      }
    } catch (e) {
      console.error(e);
      showDefaultError();
      reportErrorToSentry(e);
    }
  };

  const onChange = useCallback(
    debounce(() => {
      getAutofillSuggestions();
    }, 500),
    []
  );

  const setZipCode = (ev: any) => {
    zipCode.current = ev.target.value;
    onChange();
  };

  const setHouseNumber = (ev: any) => {
    houseNumber.current = ev.target.value;
    onChange();
  };

  const zipCodeField = (
    <div className="form__field-container">
      <Field
        onKeyUp={setZipCode}
        name={`${props.context}.zipCode`}
        component={TextField}
        label={formatMessage({ id: 'address.zipCode' })}
        maxLength={zipCodeConfig.maxLength ?? 8}
        validate={getValidator({ required: true, minLength: 3, format: 'default', ...zipCodeConfig })}
        data-test-id={`${props.context}-zipCode`}
      />
    </div>
  );

  const houseNumberField = (
    <div className="form__field-container">
      <Field
        onKeyUp={setHouseNumber}
        name={`${props.context}.houseNumber`}
        component={TextField}
        label={formatMessage({ id: 'address.houseNumber' })}
        maxLength={houseNumberConfig.maxLength ?? 10}
        validate={getValidator({
          required: true,
          minLength: 1,
          format: /^\d+[-/]?(\d+)?([a-z]+)?/i,
          ...houseNumberConfig,
        })}
        data-test-id={`${props.context}-houseNumber`}
      />
    </div>
  );

  return (
    <>
      <div className="form__row mobile-break">
        <div className="form__field-split">
          {zipCodeField}
          {houseNumberField}
        </div>
        <div className="form__field-container">
          <Field
            name={`${props.context}.additional`}
            component={TextField}
            label={`${formatMessage({ id: 'address.additional' })} (${formatMessage({ id: 'generic.optional' })})`}
            maxLength={additionalConfig.maxLength ?? 60}
            validate={getValidator({ format: 'default', ...additionalConfig })}
            data-test-id={`${props.context}-additional`}
          />
        </div>
      </div>
      <div className="form__row">
        <div className="form__field-container">
          <Field
            name={`${props.context}.street`}
            component={TextField}
            label={formatMessage({ id: 'address.street' })}
            maxLength={streetConfig?.maxLength ?? 50}
            validate={getValidator({
              required: true,
              minLength: 3,
              format: 'streetOrCity',
              ...streetConfig,
            })}
            data-test-id={`${props.context}-street`}
          />
        </div>
      </div>
      <div className="form__row">
        <div className="form__field-container">
          <Field
            name={`${props.context}.city`}
            component={TextField}
            label={formatMessage({ id: 'address.city' })}
            maxLength={cityConfig?.maxLength ?? 50}
            validate={getValidator({
              required: true,
              minLength: 3,
              format: 'streetOrCity',
              ...cityConfig,
            })}
            data-test-id={`${props.context}-city`}
          />
        </div>
      </div>
    </>
  );
};

export default AdressFieldNl;
